import { Modal, useModal } from '@billy/design-system'
import React, { ReactElement, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Contact } from '@features/contacts/types/contact'

import { ModalId } from '../../../../../enums/modalId'
import { ContactModalContextProvider } from '../../contexts/contactModalContext'
import { ContactTypeSwitcher } from '../ContactTypeSwitcher'
import { ModalFooter } from '../ModalFooter'
import { SupplierForm } from '../SupplierForm'

interface LegacyCreateContactModalProps {
  defaultValues?: Partial<Contact>
  isSupplier: boolean
  onContactCreate?: (contact: Contact) => void
}

const AUTO_FOCUS_TIMEOUT = 1000

export const LegacyCreateContactModal = ({
  defaultValues,
  isSupplier,
  onContactCreate,
}: LegacyCreateContactModalProps): ReactElement => {
  const { t } = useTranslation()
  const modalRef = useRef<HTMLDivElement>(null)
  const { isOpen } = useModal(ModalId.CreateContactModal)

  // Autofocus on first input if contact is not in the organization(defaultValues)
  const autoFocusOnFirstInput = !(defaultValues?.registrationNo && !defaultValues?.id)
  const modalTitle = isSupplier ? t('create_contact_modal.title.vendor') : t('create_contact_modal.title.client')

  useEffect(() => {
    if (isOpen && !autoFocusOnFirstInput) {
      setTimeout(() => {
        const inputs = modalRef?.current?.getElementsByTagName('input')

        // Focus on second input
        inputs?.[1]?.focus()
      }, AUTO_FOCUS_TIMEOUT)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- focus the second input only when the modal opens, not when the prefilled values change
  }, [isOpen])

  return (
    <Modal autofocusOnInput={autoFocusOnFirstInput} id={ModalId.CreateContactModal} ref={modalRef} size="s">
      <ContactModalContextProvider
        defaultValues={defaultValues}
        isSupplier={isSupplier}
        onContactCreate={onContactCreate}
      >
        <Modal.Header closable id={ModalId.CreateContactModal} title={modalTitle}>
          <ContactTypeSwitcher />
        </Modal.Header>
        <Modal.Body>{isSupplier && <SupplierForm />}</Modal.Body>
        <Modal.Footer>
          <ModalFooter />
        </Modal.Footer>
      </ContactModalContextProvider>
    </Modal>
  )
}
